/* ============================================================================
 * Movatic WMS — Frontend | CesarFilho 932048016
 * Arquivo: src/pages/partners/SupplierIssues.tsx
 * ----------------------------------------------------------------------------
 * Lista de Ocorrências de Fornecedor (acompanhamento).
 * - Suporta filtro por ?supplier=<id>&supplierName=<txt>
 * - Endpoints compat: /partners/issues/ (preferido) OU /partners/supplier-issues/
 * - DRF paginado ({count, results}) ou lista direta ([]).
 * - Coluna "Ações" com botão "Anexos" que abre modal (IssueAttachmentsModal).
 * ========================================================================== */

import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams, useNavigate } from "react-router-dom";
import { apiGet } from "@/lib/api";
import IssueAttachmentsModal from "@/pages/partners/components/IssueAttachmentsModal";

type IssueItem = {
    id: number;
    supplier: number;
    supplier_name?: string | null;
    issue_type?: number | null;
    severity?: number | null;
    status?: "OPEN" | "SENT" | "CLOSED" | string;
    description?: string | null;
    due_date?: string | null;
    assigned_to?: number | null;
    assigned_to_name?: string | null;
    created_at?: string | null;
    attachments_count?: number;
};

type Paginated<T> = { count: number; next: string | null; previous: string | null; results: T[] };

function cls(...xs: Array<string | false | null | undefined>) {
    return xs.filter(Boolean).join(" ");
}
function fmtDate(iso?: string | null) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString();
    } catch {
        return iso;
    }
}
function badgeStatus(s?: string) {
    const base = "inline-flex items-center rounded px-2 py-0.5 text-xs font-medium";
    switch (s) {
        case "OPEN":
            return base + " bg-yellow-900/30 text-yellow-300";
        case "SENT":
            return base + " bg-blue-900/30 text-blue-300";
        case "CLOSED":
            return base + " bg-green-900/30 text-green-300";
        default:
            return base + " bg-neutral-700/60 text-neutral-200";
    }
}

async function fetchIssues(params: {
    search: string;
    ordering: string;
    supplier?: number | string | null;
    page?: number;
    pageSize?: number;
}) {
    const endpoints = ["/partners/issues/", "/partners/supplier-issues/"];
    const { search, ordering, supplier, page = 1, pageSize = 50 } = params;

    for (const path of endpoints) {
        try {
            const res = await apiGet<any>(path, {
                params: {
                    search: search || undefined,
                    ordering: ordering || undefined,
                    page,
                    page_size: pageSize,
                    supplier: supplier || undefined, // << filtro por fornecedor
                },
            });
            const data = res.data;
            const items: IssueItem[] = Array.isArray(data)
                ? data
                : Array.isArray((data as Paginated<IssueItem>)?.results)
                    ? (data as Paginated<IssueItem>).results
                    : [];
            if (items.length || Array.isArray(data) || (data && typeof data === "object" && "results" in data)) {
                const total = (data && (data as Paginated<IssueItem>).count) ?? items.length;
                return { items, total, endpoint: path };
            }
        } catch {
            // tenta o próximo endpoint
        }
    }
    throw new Error("Não foi possível carregar ocorrências.");
}

export default function SupplierIssuesPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();

    const supplierParam = params.get("supplier");
    const supplierName = params.get("supplierName") || "";
    const supplierId = supplierParam ? Number(supplierParam) || supplierParam : undefined;

    const [term, setTerm] = useState("");
    const [ordering, setOrdering] = useState<string>("-created_at");

    const q = useQuery({
        queryKey: ["partners-issues", term, ordering, supplierId],
        queryFn: () =>
            fetchIssues({
                search: term,
                ordering,
                supplier: supplierId,
                page: 1,
                pageSize: 200,
            }),
        staleTime: 30_000,
    });

    const rows = useMemo(() => q.data?.items ?? [], [q.data]);

    const clearSupplier = () => {
        params.delete("supplier");
        params.delete("supplierName");
        navigate({ search: params.toString() || "" }, { replace: true });
    };

    return (
        <div className="p-4 text-neutral-100">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="text-xl font-semibold">Ocorrências de Fornecedor</h1>
                    <p className="mt-1 text-sm text-neutral-400">
                        Acompanhe status, severidade, responsável, prazo e anexos (fotos).
                    </p>
                    {supplierId && (
                        <div className="mt-2 inline-flex items-center gap-2 rounded bg-neutral-900 px-2 py-1 text-xs">
                            <span className="text-neutral-300">Fornecedor:</span>
                            <strong className="text-neutral-100">
                                {supplierName ? `${supplierName} (#${supplierId})` : `#${supplierId}`}
                            </strong>
                            <button
                                className="ml-1 rounded bg-neutral-800 px-2 py-0.5 text-neutral-300 hover:bg-neutral-700"
                                onClick={clearSupplier}
                                title="Limpar filtro"
                            >
                                limpar
                            </button>
                        </div>
                    )}
                </div>

                <div className="flex flex-col items-stretch gap-2 sm:flex-row">
                    <input
                        value={term}
                        onChange={(e) => setTerm(e.target.value)}
                        placeholder="Buscar (fornecedor, descrição...)"
                        className="h-9 w-72 rounded-md border border-neutral-700 bg-neutral-900 px-3 text-sm outline-none placeholder:text-neutral-500"
                    />
                    <select
                        value={ordering}
                        onChange={(e) => setOrdering(e.target.value)}
                        className="h-9 rounded-md border border-neutral-700 bg-neutral-900 px-2 text-sm"
                        title="Ordenar"
                    >
                        <option value="-created_at">Mais recentes</option>
                        <option value="created_at">Mais antigas</option>
                        <option value="-severity">Severidade (desc)</option>
                        <option value="severity">Severidade (asc)</option>
                        <option value="due_date">Prazo (asc)</option>
                        <option value="-due_date">Prazo (desc)</option>
                    </select>
                    <button
                        onClick={() => q.refetch()}
                        className="h-9 rounded-md border border-neutral-700 bg-neutral-900 px-3 text-sm"
                    >
                        Atualizar
                    </button>
                </div>
            </div>

            {q.isLoading && (
                <div className="rounded-md border border-neutral-800 bg-neutral-900 p-6">Carregando ocorrências…</div>
            )}
            {q.isError && (
                <div className="rounded-md border border-red-800 bg-red-950/40 p-4 text-red-300">
                    {(q.error as Error)?.message || "Erro ao carregar ocorrências."}
                </div>
            )}

            {!q.isLoading && !q.isError && (
                <div className="overflow-auto rounded-md border border-neutral-800">
                    <table className="min-w-[1100px] w-full text-sm">
                        <thead className="bg-neutral-900">
                            <tr className="text-left">
                                <Th>ID</Th>
                                <Th>Fornecedor</Th>
                                <Th>Tipo</Th>
                                <Th>Sev.</Th>
                                <Th>Status</Th>
                                <Th>Responsável</Th>
                                <Th>Prazo</Th>
                                <Th>Descrição</Th>
                                <Th>Anexos</Th>
                                <Th>Criado em</Th>
                                <Th className="text-right">Ações</Th>
                            </tr>
                        </thead>
                        <tbody className="bg-black">
                            {rows.length === 0 && (
                                <tr>
                                    <td colSpan={11} className="p-6 text-center text-neutral-400">
                                        Nenhuma ocorrência encontrada.
                                    </td>
                                </tr>
                            )}
                            {rows.map((it) => (
                                <tr key={it.id} className="border-t border-neutral-800 hover:bg-neutral-900/40">
                                    <Td className="whitespace-nowrap">#{it.id}</Td>
                                    <Td className="whitespace-nowrap">{it.supplier_name || `Supplier ${it.supplier}`}</Td>
                                    <Td className="whitespace-nowrap">{it.issue_type ?? "—"}</Td>
                                    <Td className="whitespace-nowrap">{it.severity ?? "—"}</Td>
                                    <Td>
                                        <span className={badgeStatus(it.status)}>{it.status || "—"}</span>
                                    </Td>
                                    <Td className="whitespace-nowrap">{it.assigned_to_name || "—"}</Td>
                                    <Td className="whitespace-nowrap">
                                        {it.due_date ? new Date(it.due_date).toLocaleDateString() : "—"}
                                    </Td>
                                    <Td className="max-w-[360px] truncate" title={it.description ?? ""}>
                                        {it.description || "—"}
                                    </Td>
                                    <Td className="whitespace-nowrap">
                                        {typeof it.attachments_count === "number" ? it.attachments_count : 0}
                                    </Td>
                                    <Td className="whitespace-nowrap">{fmtDate(it.created_at)}</Td>
                                    <Td className="text-right">
                                        <IssueAttachmentsModal
                                            issueId={it.id}
                                            count={typeof it.attachments_count === "number" ? it.attachments_count : undefined}
                                            buttonClassName="btn btn-xs btn-outline"
                                            label="Anexos"
                                        />
                                    </Td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {!q.isLoading && !q.isError && (
                <div className="mt-3 text-xs text-neutral-400">
                    {rows.length} item(ns)
                    {q.data?.endpoint ? ` • fonte: ${q.data.endpoint}` : ""}
                </div>
            )}
        </div>
    );
}

function Th({ children, className = "", ...rest }: React.ThHTMLAttributes<HTMLTableCellElement>) {
    return (
        <th
            className={cls("px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-300", className)}
            {...rest}
        >
            {children}
        </th>
    );
}
function Td({ children, className = "", ...rest }: React.TdHTMLAttributes<HTMLTableCellElement>) {
    return <td className={cls("px-3 py-2 align-top text-neutral-100", className)} {...rest}>{children}</td>;
}
